'use client'

import { Calendar, Popover, PopoverContent, PopoverTrigger } from '@repo/ui'
import { CalendarDays } from 'lucide-react'
import { useState } from 'react'

import { formatDay, fromISO, todayISO, toISO } from '@/lib/dates'
import { RADIUS, SURFACE_SOLID } from '@/lib/surface'
import { BODY } from '@/lib/typography'
import { cn } from '@/lib/utils'

/*
 * There used to be a `DateRangePicker` here — a two-month range calendar with
 * a live day count and a price line under it — plus the `RangeCalendar` only
 * it mounted and the `DateRangeValue` type only it took. Nothing rendered any
 * of it: the rental term has been chosen by `RentalWindowPicker` since it
 * learned that FLOU sells five fixed lengths rather than an arbitrary span, so
 * a free-range calendar was offering terms the checkout refuses. A hundred and
 * seventy lines, and `react-day-picker` in `range` mode pulled into a bundle
 * nobody opened.
 */

/**
 * One calendar day — a shoot, or the day an hour in a room is booked on.
 *
 * Not an `<input type="date">`, which renders whatever the OS decides: a
 * `dd/mm/yyyy` stub next to a full-screen wheel, in the browser's language
 * instead of the page's.
 */
export function DayPicker({
  value,
  onChange,
  placeholder = 'Wybierz dzień',
  min,
  className,
  id,
}: {
  value: string
  onChange: (next: string) => void
  placeholder?: string
  min?: string
  className?: string
  id?: string
}) {
  const [open, setOpen] = useState(false)
  const floor = min || todayISO()
  const start = fromISO(floor)
  const selected = fromISO(value) ?? undefined

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={id}
        aria-label={value ? `Dzień: ${formatDay(value)}` : placeholder}
        className={cn(
          BODY,
          'flex min-h-12 w-full items-center gap-2.5 rounded-2xl border border-white/15 bg-black/40 px-4 text-left transition outline-none hover:border-white/25 focus-visible:border-white/40 focus-visible:ring-2 focus-visible:ring-white/25 data-[state=open]:border-white/40',
          className,
        )}
      >
        <CalendarDays size={16} aria-hidden="true" className="shrink-0 text-white/55" />
        <span className={cn('flex-1 truncate', value ? 'text-white' : 'text-white/55')}>
          {value ? formatDay(value) : placeholder}
        </span>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className={cn('w-auto p-0 text-white', 'z-[100]', RADIUS.box, SURFACE_SOLID)}
        collisionPadding={16}
      >
        <Calendar
          mode="single"
          numberOfMonths={1}
          autoFocus
          selected={selected}
          onSelect={(next) => {
            // Closing on pick is right for a single day and wrong for a range:
            // there is no second end to wait for.
            onChange(next ? toISO(next) : '')
            if (next) setOpen(false)
          }}
          {...(start ? { startMonth: start, disabled: { before: start } } : {})}
          {...((selected ?? start) ? { defaultMonth: selected ?? start ?? undefined } : {})}
          className="[--cell-size:2.5rem] sm:[--cell-size:2.35rem]"
          classNames={{
            weekday: 'flex-1 text-[0.75rem] font-medium text-white/55 capitalize select-none',
            caption_label: 'text-[0.9rem] font-semibold capitalize select-none',
            outside: 'text-white/55',
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
